import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { SyntheticEvent, MouseEvent, useEffect } from 'react'

import Skeleton from 'app/components/base/Skeleton'
import { MobileTabsProps } from 'app/features/Blog/components/MobileTabs'
import { useGetBlogArticleCategories } from 'app/services/Query/blog/blog.query'
import { ROOT_CATEGORY_TAB } from 'app/utils/constants/blog.constants'
import {
  prependRootTab,
  convertToBlogOptionModel,
  isDefaultCategory
} from 'app/utils/helpers/blog.helpers'
import { isOfType } from 'app/utils/helpers/type.helpers'

import DesktopTabs, { DesktopTabsProps } from './components/DesktopTabs'

const MobileTabs = dynamic(
  () => import('app/features/Blog/components/MobileTabs'),
  { ssr: true }
)

export interface ArticleTabsProps {
  isMobile: boolean
  excludeRootTab?: boolean
  sx?: DesktopTabsProps['sx'] | MobileTabsProps['sx']
}

const ArticleTabs: RFC<ArticleTabsProps> = ({
  isMobile,
  excludeRootTab = false,
  sx
}) => {
  const { isFallback, push, prefetch, query } = useRouter()
  const queryCategory = query?.category ?? ''
  const category = isOfType.string(queryCategory) ? queryCategory : ''

  const { data = [] } = useGetBlogArticleCategories({
    options: {
      enabled: false
    }
  })
  const tabOptions = convertToBlogOptionModel(data)
  const tabs = excludeRootTab ? tabOptions : prependRootTab(tabOptions)

  const Tabs = isMobile ? MobileTabs : DesktopTabs

  const isDefaultTab = isDefaultCategory(category)
  const selectedTab = isDefaultTab ? ROOT_CATEGORY_TAB.value : category

  const handleChange = (
    event: MouseEvent<HTMLButtonElement> | SyntheticEvent<Element, Event>,
    newCategory?: string
  ) => {
    const button = event.target as HTMLButtonElement
    const category = button.value || newCategory

    if (category === ROOT_CATEGORY_TAB.value) {
      push(`/blog`, undefined, { scroll: false })
      return
    }

    push(`/blog/${category}`, undefined, { scroll: false })
  }

  useEffect(() => {
    if (!data.length) return

    data.forEach((item) => {
      prefetch(`/blog/${item.slug}`)
    })
  }, [data, prefetch])

  return (
    <Skeleton loading={isFallback} width="80%" height="48px">
      <Tabs
        value={selectedTab}
        onTabChange={handleChange}
        tabs={tabs}
        sx={sx}
      />
    </Skeleton>
  )
}
export default ArticleTabs
